/**
 * The typed fetch wrapper.
 *
 * Everything that talks to the backend goes through `api`. That gives one
 * place to enforce the two properties that matter:
 *
 * **Errors keep their structure.** The backend answers every 4xx/5xx with
 * `{"error": {code, message, context, request_id}}`. A wrapper that throws
 * `new Error(response.statusText)` discards the message written for a human
 * and the request id needed to find the server log line. `ApiError` carries
 * all four.
 *
 * **Nothing is silently coerced.** No date parsing, no Decimal→number, no
 * key renaming. `types.ts` is the contract; if a field is a string on the
 * wire it is a string here, and it becomes a number only in `format.ts` at
 * the moment of drawing.
 *
 * Base URL is `/api` — relative, same-origin, proxied by Vite in dev. There
 * is deliberately no env var: this platform is self-hosted and single-user,
 * and a configurable API host is one more way to point a LIVE terminal at
 * the wrong process.
 */

import type { ErrorBody, ErrorResponse, JsonObject } from './types';
import { demoNamesActive, fromDemo, matchesDemoSearch, noteEnvironment, toDemo } from '@/lib/demoNames';

export const API_BASE = '/api';

/** Header the backend echoes on every response. Quote it in a bug report. */
export const REQUEST_ID_HEADER = 'X-Request-ID';

/**
 * The shared secret `/api/db/*` requires, and the header it travels in.
 *
 * **Why an env var here, when the file above says there is deliberately
 * none.** That paragraph is about the API *host*, and it still stands — a
 * configurable backend URL is a way to point a LIVE terminal at the wrong
 * process. This is not a host. It is a secret, and a secret cannot be a
 * literal in a file that goes into git (E3).
 *
 * **Attached in `request()` by path prefix, not passed per call site.** Every
 * `/db/` request needs it and none of the others do; a header threaded
 * through four hooks is a header the fifth hook forgets, on a route that
 * deletes rows. One place, and it cannot be omitted.
 *
 * Absent at build time it is the empty string, the backend answers 401, and
 * `Database.tsx` renders a specific message naming this variable — which is
 * the whole reason the value is not silently defaulted to something.
 */
export const DB_TOKEN_HEADER = 'X-Bheema-DB-Token';

const DB_TOKEN: string = import.meta.env.VITE_BHEEMA_DB_TOKEN ?? '';

/** Paths under `/api` that carry the database-browser secret. */
function needsDbToken(path: string): boolean {
  return path.startsWith('/db/') || path === '/db';
}

/**
 * CSRF double-submit. The backend sets a readable `bheema_csrf` cookie on login;
 * every unsafe request echoes it in this header, and the server compares it to
 * the value bound to the session (app.auth). Attached here by method, in one
 * place, so no mutating call site can forget it.
 */
export const CSRF_HEADER = 'X-CSRF-Token';
const CSRF_COOKIE = 'bheema_csrf';
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readCookie(name: string): string {
  const prefix = `${name}=`;
  for (const part of document.cookie ? document.cookie.split('; ') : []) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return '';
}

/**
 * Called when the API answers 401 on a non-auth path — the session expired or
 * was revoked. The AuthProvider registers a handler that re-checks the session,
 * which drops the app back to the login screen. Set once at startup.
 */
let onUnauthorized: (() => void) | null = null;
export function setOnUnauthorized(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/**
 * A structured failure from the backend, or from the network.
 *
 * `code` is machine-readable and stable (`not_found`, `limit_breached`,
 * `kill_switch`, `validation_error`, `internal_error`, plus the transport
 * codes below). Branch on `code`, never on `message`.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly context: JsonObject;
  readonly requestId: string;
  /** True for network/abort/parse failures — the request never got an answer. */
  readonly isTransport: boolean;

  constructor(init: {
    code: string;
    message: string;
    status: number;
    context?: JsonObject;
    requestId?: string;
    isTransport?: boolean;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.context = init.context ?? {};
    this.requestId = init.requestId ?? '';
    this.isTransport = init.isTransport ?? false;
  }

  /** True when retrying could plausibly succeed. */
  get isRetryable(): boolean {
    if (this.isTransport) return true;
    return this.status === 429 || (this.status >= 500 && this.status !== 501);
  }

  /** True when the platform said "no" for a safety reason, not a bug. */
  get isSafetyRefusal(): boolean {
    return this.code === 'kill_switch' || this.code === 'limit_breached';
  }

  /** One line fit for a toast, with the request id when there is one. */
  toDisplayString(): string {
    return this.requestId ? `${this.message} (ref ${this.requestId})` : this.message;
  }
}

function isErrorResponse(value: unknown): value is ErrorResponse {
  if (typeof value !== 'object' || value === null) return false;
  const error = (value as { error?: unknown }).error;
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as ErrorBody).code === 'string' &&
    typeof (error as ErrorBody).message === 'string'
  );
}

/**
 * Query-string values.
 *
 * `undefined` and `null` are dropped so callers can spread an options object
 * without building the string by hand. Arrays repeat the key, which is what
 * FastAPI expects for `run_ids=1&run_ids=2`. `false` is *kept* — dropping it
 * would silently flip a boolean filter's meaning.
 */
export type QueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>;

export type QueryParams = Record<string, QueryValue>;

export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.append(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export interface RequestOptions {
  query?: QueryParams;
  signal?: AbortSignal;
  /** Milliseconds before the request is aborted. Default 60s; sweeps use more. */
  timeoutMs?: number;
  headers?: Record<string, string>;
}

interface FullRequestOptions extends RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

const DEFAULT_TIMEOUT_MS = 60_000;

/** A successful answer, with the demo display names applied (lib/demoNames). */
function answered<T>(path: string, body: unknown, demoSearch: string | null): T {
  if (path.split('?')[0] === '/system/environment' && typeof body === 'object' && body !== null) {
    noteEnvironment((body as { is_live?: unknown }).is_live === true);
  }
  const renamed = toDemo(body);
  if (demoSearch === null || typeof renamed !== 'object' || renamed === null) return renamed as T;
  // The server searched nothing (see `request`): filter by the demo names here.
  const page = renamed as { items?: unknown; total?: unknown };
  if (!Array.isArray(page.items)) return renamed as T;
  const items = page.items.filter((item: unknown) => {
    const symbol = (item as { symbol?: unknown } | null)?.symbol;
    return typeof symbol === 'string' && matchesDemoSearch(symbol, demoSearch);
  });
  return { ...page, items, ...(typeof page.total === 'number' ? { total: items.length } : {}) } as T;
}

async function request<T>(path: string, options: FullRequestOptions): Promise<T> {
  // Demo display names: the server only ever sees the real tickers.
  path = fromDemo(path, path);
  // Symbol search while demo names are on: the server matches real tickers,
  // so it is asked for the whole list and `answered` filters by demo names.
  const typed = options.query?.['q'];
  const demoSearch =
    demoNamesActive() && path === '/market/symbols' && typeof typed === 'string' ? typed : null;
  const query = demoSearch === null ? options.query : { ...options.query, q: undefined };
  const url = `${API_BASE}${path}${buildQuery(fromDemo(query, path))}`;

  // Compose the caller's signal with our timeout, so a component unmounting
  // and a slow backend both abort the same request exactly once. Keep both
  // active through body consumption: fetch resolves at headers, not at EOF.
  // Otherwise a stalled JSON body can preserve stale financial data forever [S4].
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(new DOMException('timeout', 'TimeoutError')),
    options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  );
  const onExternalAbort = (): void => controller.abort(options.signal?.reason);
  if (options.signal) {
    if (options.signal.aborted) controller.abort(options.signal.reason);
    else options.signal.addEventListener('abort', onExternalAbort, { once: true });
  }

  let response: Response | undefined;
  let requestId = "";
  try {
    response = await fetch(url, {
      method: options.method,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        // Sent even when empty, so the backend distinguishes "this client
        // knows about the scheme and was built without a token" from "this
        // client has never heard of it" in its own logs. Both are 401.
        ...(needsDbToken(path) ? { [DB_TOKEN_HEADER]: DB_TOKEN } : {}),
        // CSRF double-submit on every state-changing request (harmless on the
        // allowlisted auth POSTs; required on authenticated ones).
        ...(UNSAFE_METHODS.has(options.method) ? { [CSRF_HEADER]: readCookie(CSRF_COOKIE) } : {}),
        ...options.headers,
      },
      body: options.body === undefined ? undefined : JSON.stringify(fromDemo(options.body, path)),
    });

    requestId = response.headers.get(REQUEST_ID_HEADER) ?? '';

    if (response.status === 204) return undefined as T;

    // Success parses straight off the response stream. `text()` then
    // `JSON.parse()` materialises the whole body as a JS string first, which
    // for a candle payload is a few hundred kilobytes copied and decoded for
    // nothing. Failures still go through `text()`, because an error body may
    // not be JSON at all and the raw text is what gets reported.
    if (response.ok) {
      try {
        return answered<T>(path, await response.json(), demoSearch);
      } catch (cause) {
        if (controller.signal.aborted) throw cause;
        throw new ApiError({
          code: 'bad_response',
          message: 'the backend returned a body that is not JSON',
          status: response.status,
          requestId,
          context: { url, cause: cause instanceof Error ? cause.message : String(cause) },
        });
      }
    }

    const text = await response.text();
    let parsed: unknown = undefined;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text) as unknown;
      } catch {
        if (!response.ok) {
          throw new ApiError({
            code: 'bad_response',
            message: `the backend returned ${response.status} with a non-JSON body`,
            status: response.status,
            requestId,
            context: { body: text.slice(0, 400) },
          });
        }
        throw new ApiError({
          code: 'bad_response',
          message: 'the backend returned a body that is not JSON',
          status: response.status,
          requestId,
          context: { body: text.slice(0, 400) },
        });
      }
    }

    if (!response.ok) {
      // A 401 on a non-auth path means the session lapsed mid-use; nudge the
      // AuthProvider to re-check so the app drops to /login rather than showing
      // a wall of failed panels. The auth endpoints manage their own state.
      if (response.status === 401 && !path.startsWith('/auth/') && onUnauthorized) {
        onUnauthorized();
      }
      if (isErrorResponse(parsed)) {
        throw new ApiError({
          code: parsed.error.code,
          message: toDemo(parsed.error.message),
          status: response.status,
          context: toDemo(parsed.error.context ?? {}),
          requestId: parsed.error.request_id || requestId,
        });
      }
      throw new ApiError({
        code: `http_${response.status}`,
        message: `the backend returned ${response.status} ${response.statusText}`.trim(),
        status: response.status,
        requestId,
        context: typeof parsed === 'object' && parsed !== null ? (parsed as JsonObject) : {},
      });
    }

    return answered<T>(path, parsed, demoSearch);
  } catch (cause) {
    if (cause instanceof ApiError) throw cause;
    const aborted = controller.signal.aborted;
    const isTimeout =
      aborted && (controller.signal.reason as DOMException | undefined)?.name === 'TimeoutError';
    throw new ApiError({
      code: isTimeout ? 'timeout' : aborted ? 'aborted' : 'network_error',
      message: isTimeout
        ? `the backend did not answer within ${(options.timeoutMs ?? DEFAULT_TIMEOUT_MS) / 1000}s`
        : aborted
          ? 'request cancelled'
          : 'could not reach the backend — is it running?',
      status: response?.status ?? 0,
      requestId,
      isTransport: true,
      context: { url, cause: cause instanceof Error ? cause.message : String(cause) },
    });
  } finally {
    window.clearTimeout(timeout);
    if (options.signal) options.signal.removeEventListener('abort', onExternalAbort);
  }
}

export const api = {
  get: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> =>
    request<T>(path, { ...options, method: 'POST', body: body ?? {} }),

  put: <T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> =>
    request<T>(path, { ...options, method: 'PUT', body: body ?? {} }),

  patch: <T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> =>
    request<T>(path, { ...options, method: 'PATCH', body: body ?? {} }),

  delete: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/**
 * Normalise anything thrown into an `ApiError`.
 *
 * For error boundaries and toast handlers, so a rendering bug and a backend
 * refusal are displayed with the same shape instead of one of them producing
 * `[object Object]`.
 */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof Error) {
    return new ApiError({
      code: 'client_error',
      message: error.message,
      status: 0,
      isTransport: true,
    });
  }
  return new ApiError({
    code: 'unknown_error',
    message: String(error),
    status: 0,
    isTransport: true,
  });
}

/** URL of the multiplexed WebSocket, same origin as the page. */
export function websocketUrl(topics: readonly string[] = []): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const query = topics.length > 0 ? `?topics=${topics.map(encodeURIComponent).join(',')}` : '';
  return `${protocol}//${window.location.host}/ws${query}`;
}
